import {
  daysBetween,
  moneyChangeRatio,
  type Money,
  type Ratio,
  signOfMoney,
  subtractMoney,
} from '@taler/domain'
import type { NetWorthPoint } from './types.ts'

export const TARGET_CHANGE_DAYS = 7

/** Beyond this a percentage against a near-zero base reads as noise, not information. */
const MAX_MEANINGFUL_PERCENT = 1000n * 10n ** 10n

export interface KpiChange {
  readonly delta: Money
  readonly direction: -1 | 0 | 1
  /** Undefined whenever the base is zero, negative, or too small to make the ratio meaningful. */
  readonly percent: Ratio | undefined
  /** The actual span the comparison point is behind the latest one - never assumed to be {@link TARGET_CHANGE_DAYS}. */
  readonly days: number
}

/** A change of a figure with no money delta (a distance count): only its percentage and direction exist. */
export interface PercentChange {
  readonly direction: -1 | 0 | 1
  readonly percent: Ratio
}

/**
 * Finds the point closest to `targetDays` before the series' latest point and compares
 * `pick`'s figure there against the latest. Needs at least two points; returns `undefined`
 * otherwise, since there is nothing to compare against.
 */
export function computeKpiChange(
  points: readonly NetWorthPoint[],
  pick: (point: NetWorthPoint) => Money,
  targetDays: number = TARGET_CHANGE_DAYS,
): KpiChange | undefined {
  if (points.length < 2) return undefined
  const latest = points[points.length - 1]!
  let comparison = points[0]!
  let bestDiff = Math.abs(daysBetween(comparison.date, latest.date) - targetDays)
  for (const point of points.slice(0, -1)) {
    const diff = Math.abs(daysBetween(point.date, latest.date) - targetDays)
    if (diff < bestDiff) {
      comparison = point
      bestDiff = diff
    }
  }

  return changeBetween(pick(comparison), pick(latest), daysBetween(comparison.date, latest.date))
}

/** The percentage guard every comparison shares: beyond it a change against a near-zero base is noise. */
export function meaningfulPercent(percent: Ratio | undefined): Ratio | undefined {
  return percent !== undefined &&
    (percent.units < 0n ? -percent.units : percent.units) <= MAX_MEANINGFUL_PERCENT
    ? percent
    : undefined
}

/** The change from `base` to `current` over `days`, with the same percentage guard as a series comparison. */
export function changeBetween(base: Money, current: Money, days: number): KpiChange {
  const delta = subtractMoney(current, base)
  return {
    delta,
    direction: signOfMoney(delta),
    percent: meaningfulPercent(moneyChangeRatio(current, base)),
    days,
  }
}
